import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Scale, Shield, FileText, Globe, ArrowRight, Phone, MapPin, Gavel, Users, Briefcase, BookOpen, HandshakeIcon, ScrollText, Landmark, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LandingPage() {
  const { t, lang, toggleLang } = useLanguage();

  const [expandedService, setExpandedService] = useState(null);

  const services = [
    { icon: BookOpen, title: t('service_consultations'), desc: t('service_consultations_desc'), detail: t('service_consultations_detail') },
    { icon: ScrollText, title: t('service_contracts'), desc: t('service_contracts_desc'), detail: t('service_contracts_detail') },
    { icon: Landmark, title: t('service_procedural'), desc: t('service_procedural_desc'), detail: t('service_procedural_detail') },
    { icon: Scale, title: t('service_financial'), desc: t('service_financial_desc'), detail: t('service_financial_detail') },
    { icon: Briefcase, title: t('service_lawsuits'), desc: t('service_lawsuits_desc'), detail: t('service_lawsuits_detail') },
    { icon: Shield, title: t('service_arbitration'), desc: t('service_arbitration_desc'), detail: t('service_arbitration_detail') },
    { icon: HandshakeIcon, title: t('service_agency'), desc: t('service_agency_desc'), detail: t('service_agency_detail') },
    { icon: Users, title: t('service_labor'), desc: t('service_labor_desc'), detail: t('service_labor_detail') },
  ];

  const values = [
    { key: 'justice', icon: Scale },
    { key: 'professionalism', icon: Briefcase },
    { key: 'trust', icon: Shield },
    { key: 'confidentiality', icon: FileText },
    { key: 'excellence', icon: Gavel },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] overflow-x-hidden" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95"
              alt="Taher Nassar Law Group"
              className="h-10 sm:h-12 w-auto"
            />
            <span className="text-base sm:text-lg font-bold text-[#0F172A] font-heading hidden sm:inline">{t('firm_name_short')}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="tel:+962779787047" className="hidden md:flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] transition-colors">
              <Phone className="w-4 h-4 text-[#B48E4B]" />
              <span dir="ltr">{t('firm_phone')}</span>
            </a>
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg text-sm text-[#475569] hover:bg-slate-100 transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{lang === 'en' ? 'العربية' : 'English'}</span>
            </button>
            <Link to="/login">
              <Button variant="outline" size="sm" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white text-xs sm:text-sm" data-testid="login-nav-btn">
                {t('login')}
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-[#B48E4B] text-white hover:bg-[#8C6A32] text-xs sm:text-sm" data-testid="register-nav-btn">
                {t('get_started')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24 sm:pt-32 overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1547067592-463a85de7fd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1hcmJsZXxlbnwwfHx8fDE3NzU5NTcyNTB8MA&ixlib=rb-4.1.0&q=85)` }}
        />
        <div className={`absolute inset-0 ${lang === 'ar' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-[#0F172A]/95 via-[#0F172A]/70 to-[#0F172A]/30`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <img
                  src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95"
                  alt="Logo"
                  className="h-10 w-auto"
                />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-white font-heading mb-6">
              {t('hero_title')}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              {t('hero_subtitle')}
            </p>
            <div className="flex gap-3 sm:gap-4 flex-wrap">
              <Link to="/login">
                <Button size="lg" className="bg-[#B48E4B] text-white hover:bg-[#8C6A32] px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base" data-testid="hero-get-started-btn">
                  {t('get_started')} <ArrowRight className="w-4 h-4 ms-2" />
                </Button>
              </Link>
              <a href="tel:+962779787047">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base" data-testid="hero-contact-btn">
                  <Phone className="w-4 h-4 me-2" /> {t('call_us')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 sm:py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#B48E4B] mb-4">{t('about_title')}</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight text-[#0F172A] font-heading mb-6">
                {t('about_title')}
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-[#475569] mb-4">
                {t('about_desc')}
              </p>
              <p className="text-sm sm:text-base leading-relaxed text-[#475569] mb-6">
                {t('about_team')}
              </p>
              {/* Vision */}
              <div className="bg-[#0F172A] rounded-xl p-6 mb-6">
                <h3 className="text-lg font-medium text-[#B48E4B] font-heading mb-2">{t('vision_title')}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{t('vision_desc')}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://images.pexels.com/photos/7841469/pexels-photo-7841469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Team"
                  className="w-full h-[250px] sm:h-[300px] object-cover"
                />
              </div>
              {/* Values */}
              <div className="grid grid-cols-1 gap-3">
                {values.map((v) => (
                  <div key={v.key} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-[#B48E4B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <v.icon className="w-4 h-4 text-[#B48E4B]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{t(`value_${v.key}`)}</p>
                      <p className="text-xs text-[#475569] mt-0.5">{t(`value_${v.key}_desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 sm:py-24 bg-[#F8F9FA]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#B48E4B] mb-4">{t('services_title')}</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight text-[#0F172A] font-heading">
              {t('services_title')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {services.map((service, i) => {
              const isExpanded = expandedService === i;
              return (
                <div
                  key={i}
                  className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all overflow-hidden cursor-pointer"
                  onClick={() => setExpandedService(isExpanded ? null : i)}
                  data-testid={`service-card-${i}`}
                >
                  <div className="p-5 sm:p-6 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-5 h-5 text-[#B48E4B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-[#0F172A] font-heading mb-1">{service.title}</h3>
                      <p className="text-sm text-[#475569] leading-relaxed">{service.desc}</p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-[#94A3B8] flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                  {isExpanded && (
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0 border-t border-slate-100">
                      <p className="text-sm text-[#475569] leading-relaxed mt-4">{service.detail}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="py-16 sm:py-24 bg-white" data-testid="offices-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#B48E4B] mb-4">{t('offices_title')}</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight text-[#0F172A] font-heading">
              {t('offices_title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { name: t('office_sweifieh'), addr: t('office_sweifieh_addr') },
              { name: t('office_russeifa'), addr: t('office_russeifa_addr') },
              { name: t('office_madaba'), addr: t('office_madaba_addr') },
            ].map((office, i) => (
              <div key={i} className="bg-slate-50 p-6 rounded-xl border border-slate-100 text-center">
                <div className="w-12 h-12 rounded-full bg-[#B48E4B]/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-[#B48E4B]" />
                </div>
                <h3 className="text-base font-medium text-[#0F172A] font-heading mb-2">{office.name}</h3>
                <p className="text-sm text-[#475569]">{office.addr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 bg-[#0F172A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl tracking-tight text-white font-heading mb-6">
            {t('cta_title')}
          </h2>
          <p className="text-base sm:text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            {t('cta_desc')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/login">
              <Button size="lg" className="bg-[#B48E4B] text-white hover:bg-[#8C6A32] px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base" data-testid="cta-get-started-btn">
                {t('get_started')} <ArrowRight className="w-4 h-4 ms-2" />
              </Button>
            </Link>
            <a href="tel:+962779787047">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base">
                <Phone className="w-4 h-4 me-2" /> <span dir="ltr">{t('phone')}</span>
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-[#020617] text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95"
                alt="Logo"
                className="h-8 w-auto"
              />
              <span className="text-white font-heading text-sm">{t('firm_name')}</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/275039159506421" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="facebook-link">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="tel:+962779787047" className="hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-800 text-center">
            <p className="text-xs sm:text-sm">
              &copy; {new Date().getFullYear()} {t('firm_name')}. {t('footer_rights')}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
